/**
 * `UIPasteboard.general` (Sources/UIKit/Pasteboard.swift): what the app
 * copies also goes to the system clipboard, through the Async Clipboard API.
 * Text is written with `writeText`; a picture as an `image/png` clipboard
 * item (the one image type browsers accept), converted from JPEG or another
 * format when needed. The write is started before the request's first
 * `await`, while the tap that copied may still count as user activation
 * (Safari requires it; Chromium wants the document focused). Reading
 * (`readText`) may ask the viewer, or be refused; the app's own pasteboard
 * keeps what it copied either way. See docs/ops-protocol.md, "Sharing and media".
 */

import type { ClipboardRequestParams } from './protocol';

/** The system clipboard; tests substitute it. */
export interface ClipboardHost {
  writeText(text: string): Promise<void>;
  /** Writes a PNG. `png` may still be on its way: the write itself must start within the tap. */
  writeImage(png: Promise<Blob>): Promise<void>;
  readText(): Promise<string>;
}

/** The browser's `navigator.clipboard`, where the page has one (a secure context). */
export function browserClipboardHost(): ClipboardHost | undefined {
  if (typeof navigator === 'undefined' || !navigator.clipboard) return undefined;
  const clipboard = navigator.clipboard;
  return {
    writeText: (text) => clipboard.writeText(text),
    writeImage: (png) =>
      typeof ClipboardItem === 'function'
        ? clipboard.write([new ClipboardItem({ 'image/png': png })])
        : Promise.reject(new Error('unsupported: this browser cannot copy images')),
    readText: () => clipboard.readText(),
  };
}

/**
 * Performs a `clipboard` request. `pictures` gathers the request's files,
 * catalog images and bitmaps (hostRequests.ts); the first becomes the
 * copied image.
 */
export async function performClipboard(
  params: ClipboardRequestParams,
  host: ClipboardHost | undefined,
  pictures: () => Promise<File[]>,
): Promise<{ text?: string }> {
  if (!host) throw new Error('unsupported: this page has no clipboard');
  if (params.action === 'readText') return { text: await host.readText() };
  if (typeof params.text === 'string') {
    await host.writeText(params.text);
    return {};
  }
  const png = pictures().then((files) => {
    const first = files[0];
    if (!first) throw new Error('unsupported: nothing to copy');
    return toPNG(first);
  });
  await host.writeImage(png);
  return {};
}

/** The picture as PNG: as it is when it is one, else drawn on a canvas and encoded. */
export async function toPNG(blob: Blob): Promise<Blob> {
  if (blob.type === 'image/png') return blob;
  const bitmap = await createImageBitmap(blob);
  if (typeof OffscreenCanvas === 'function') {
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
    return canvas.convertToBlob({ type: 'image/png' });
  }
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0);
  return new Promise((resolve, reject) =>
    canvas.toBlob((png) => (png ? resolve(png) : reject(new Error('unsupported: could not encode PNG'))), 'image/png'),
  );
}
